import {
  Box,
  Button,
  Checkbox,
  Code,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useRollbar } from "@rollbar/react";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  StripeCardElementChangeEvent,
  StripeCardElementOptions,
} from "@stripe/stripe-js";
import axios from "axios";
import { formatISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { SubmitErrorHandler, useForm, Validate } from "react-hook-form";
import { AiFillDelete } from "react-icons/ai";
import { BiLockAlt } from "react-icons/bi";
import { FaCcAmex, FaCcMastercard, FaCcVisa } from "react-icons/fa";
import FlippyStack from "../components/FlippyStack";
import SubmitButton from "../components/SubmitButton";
import { useRegistration } from "../context/Registration";
import { CustomerFormData, SetupIntentResponse } from "../models";
import { theme } from "../theme";
import {
  camelify,
  displayCents,
  getAge,
  parseSchedule,
  snakeify,
  validatePassword,
  validatePhoneNumber,
  validatePostalCode,
} from "../util";
import { StudentFormData } from "./EditStudent";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

/*
  - see existing selections
    - ability to edit (back to SelectStudents with this session selected)
    - ability to delete
  - reset offering + enrollments, back to step one (but don't reset students)
  - add emergency contacts (here or earlier?)
  - your information
*/

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc:
        "https://fonts.googleapis.com/css2?family=Montserrat:wght@500&display=swap",
    },
  ],
};

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  hidePostalCode: true,
  style: {
    base: {
      color: theme.colors.black,
      fontFamily: theme.fonts.body,
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const FLAVOUR_TEXT = "Your card will not be charged today";

// FIXME: unify this type and Student somehow
// FIXME: do this transformation when we update the values to be sent to django, like snakeify
type StudentToSend = Omit<StudentFormData, "birthdate"> & {
  birthdate: string;
};

type CompleteCheckoutData = SetupIntentResponse & {
  paymentMethodId: string;
  sessionId: number;
  students: StudentToSend[];
};

type GenericApiResponse = {};

const beginCheckout = (data: CustomerFormData) =>
  axios
    .post("/checkout/begin", snakeify(data))
    .then((res) => {
      console.log(res);
      return camelify(res.data) as SetupIntentResponse;
    })
    .catch((err) => {
      console.error(err.response);
      throw new Error(err.response.data.message);
    });

const completeCheckout = (data: CompleteCheckoutData) =>
  axios
    .post("/checkout/complete", snakeify(data))
    .then((res) => {
      console.log(res);
      return camelify(res.data) as GenericApiResponse;
    })
    .catch((err) => {
      console.error(err.response);
      throw new Error(err.response.data.message);
    });

const abandonCheckout = (data: SetupIntentResponse) =>
  axios
    .post("/checkout/abandon", snakeify(data))
    .then((res) => {
      console.log(res);
      return camelify(res.data) as GenericApiResponse;
    })
    .catch((err) => {
      console.error("abandon checkout failed", err.response);
      throw err;
    });

type PropTypes = {
  createStudent: () => void;
  editStudent: (id: number) => void;
  deleteStudent: (id: number) => void;
  changeClass: () => void;
  paymentSuccess: () => void;
  onMount?: () => void;
};

const CheckoutForm = (props: PropTypes) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [cardIsValid, setCardIsValid] = useState(false);
  const { session, offering, students, savedFormData, setSavedFormData } =
    useRegistration();

  const [checkoutError, setCheckoutError] = useState<string | undefined>(
    undefined
  );
  const clearError = () => setCheckoutError(undefined);

  // when we mount, run whatever our parent wants us to run
  const { onMount } = props;
  useEffect(() => {
    onMount && onMount();
  }, [onMount]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues: getFormValues,
  } = useForm<CustomerFormData>({
    mode: "onChange", // FIXME: performance concerns
    defaultValues: {
      addressCountry: "Canada",
      ...(savedFormData || {}),
    },
  });

  // when we dismount, save the current form data
  useEffect(
    () => () => setSavedFormData(getFormValues()),
    [setSavedFormData, getFormValues]
  );

  const rollbar = useRollbar();

  if (!session) {
    props.changeClass();
    return null;
  } else if (students.length === 0) {
    props.createStudent();
    return null;
  }

  const onSubmit = async (formData: CustomerFormData) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setSubmitting(true);

    let setupIntentResponse;
    try {
      setupIntentResponse = await beginCheckout(formData);
    } catch (e) {
      setSubmitting(false);
      if (e instanceof Error) {
        setCheckoutError(e.message);
        rollbar.error("beginCheckout failed", e);
      }
      return;
    }

    const { clientSecret, customerId } = setupIntentResponse;
    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement)!,
        billing_details: {
          name: `${formData.givenNames} ${formData.familyName}`,
          // FIXME: do we need to send the actual billing address here?
        },
      },
    });

    if (result.error) {
      // Display result.error.message in your UI.
      setSubmitting(false);
      setCheckoutError(result.error.message);
      rollbar.error("stripe.confirmCardSetup failed", null, { result });
      return;
    } else if (!result.setupIntent.payment_method) {
      setSubmitting(false);
      setCheckoutError(
        "We were unable to save your card details with Stripe. Please try again later, or contact us for assistance."
      );
      rollbar.error("Stripe did not give us back a payment method...", null, {
        result,
      });
      return;
    }

    try {
      // save the card to the user and complete registration
      await completeCheckout({
        customerId,
        clientSecret,
        paymentMethodId: result.setupIntent.payment_method,
        students: students.map((s) => ({
          ...s,
          birthdate: formatISO(s.birthdate, { representation: "date" }),
        })),
        sessionId: session!.id,
      });

      // TODO: conversion tracking: determine event shape
      (window as any).dataLayer?.push({
        event: "signup",
        planName: "Free Trial",
        planRevenue: "$0.00",
      });
      if ((window as any).hj) {
        (window as any).hj("event", "signup");
      } else {
        rollbar.error("hotjar was not available to track signup");
      }

      props.paymentSuccess();
    } catch (e) {
      console.log("checkout errored out; abandoning.", e);
      if (
        (e as any).message &&
        ((e as any).message as string).toLowerCase().includes("email")
      ) {
        rollbar.warn("completeCheckout failed (duplicate email)", e);
      } else {
        rollbar.warn("completeCheckout failed", e);
      }
      try {
        await abandonCheckout({
          customerId,
          clientSecret,
        });
      } catch (e2) {
        rollbar.error("abandonCheckout failed", e2);
      }
      if (e instanceof Error) {
        setCheckoutError(e.message ?? "An unknown error occurred.");
      }
    }
    setSubmitting(false);
  };

  const onErrors: SubmitErrorHandler<CustomerFormData> = (errors) =>
    console.error(errors);

  const onCardChanged = (event: StripeCardElementChangeEvent) => {
    setCardIsValid(event.complete && !event.error);
    if (event.error) {
      // show validation to customer
      console.error("STRIPE ERROR", event);
      setCardIsValid(false);
    }
  };

  const requiredField = (
    name: keyof CustomerFormData,
    label?: string,
    type: string = "text",
    maxLength: number = 100,
    validate?: Validate<string | undefined>,
    validationMessage?: string
  ) => (
    <FormControl id={name} isRequired isInvalid={!!errors[name]}>
      {label && (
        <FormLabel mt={2} mb={1}>
          {label}
        </FormLabel>
      )}
      <Input
        type={type}
        bgColor="white"
        {...register(name, { required: true, maxLength, validate })}
      />

      {/* use role="alert" to announce the error message */}
      {errors[name]?.type === "required" && (
        <FormErrorMessage>This is required.</FormErrorMessage>
      )}
      {errors[name]?.type === "maxLength" && (
        <FormErrorMessage>Maximum length exceeded.</FormErrorMessage>
      )}
      {errors[name]?.type === "validate" && (
        <FormErrorMessage>
          {validationMessage || "Invalid value."}
        </FormErrorMessage>
      )}
    </FormControl>
  );

  const optionalField = (
    name: keyof CustomerFormData,
    label?: string,
    type: string = "text",
    maxLength: number = 100
  ) => (
    <FormControl id={name} isInvalid={!!errors[name]}>
      {label && (
        <FormLabel mt={2} mb={1}>
          {label}
        </FormLabel>
      )}
      <Input
        type={type}
        bgColor="white"
        {...register(name, { required: false, maxLength })}
      />

      {errors[name]?.type === "maxLength" && (
        <FormErrorMessage>Maximum length exceeded.</FormErrorMessage>
      )}
    </FormControl>
  );

  const { course } = offering!;
  const schedules = offering!.schedules.map(parseSchedule);
  const subTotal = course.amountCents * students.length;
  const taxAmount = course.taxType ? course.taxCents! * students.length : 0;
  const total = subTotal + taxAmount;
  return (
    <>
      <Heading size="md">Your selection</Heading>
      <Flex mt={2} mb={4}>
        <Text>
          {course.name}: {schedules.join(", ")}
        </Text>
        <Button size="xs" onClick={props.changeClass} ml={2}>
          Change
        </Button>
      </Flex>

      <Box ml={{ base: -4, md: 0 }} mr={{ base: -4, md: 0 }} mb={8}>
        <Table
          variant="simple"
          size="sm"
          w={{ base: "unset", md: "100%" }}
          m="0 auto"
        >
          <TableCaption>
            After the trial period, your card will be charged this amount each
            month.
          </TableCaption>
          <Thead>
            <Tr>
              <Th w="60%">Students</Th>
              <Th></Th>
              <Th textAlign="right">Price</Th>
            </Tr>
          </Thead>
          <Tbody>
            {students.map((s) => (
              <Tr key={s.id}>
                <Td colSpan={2}>
                  <IconButton
                    aria-label={`Remove ${s.givenNames} ${s.familyName}`}
                    size="lg"
                    h={6}
                    minW={6}
                    mr={2}
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => props.deleteStudent(s.id)}
                    icon={<AiFillDelete />}
                    verticalAlign="-3px"
                  />
                  <Button
                    variant="link"
                    colorScheme="blue"
                    onClick={() => props.editStudent(s.id)}
                  >
                    {s.givenNames} {s.familyName} ({getAge(s.birthdate)}yo)
                  </Button>
                </Td>
                <Td textAlign="right">
                  {displayCents(course.amountCents, course.currencyCode)}
                </Td>
              </Tr>
            ))}
          </Tbody>
          <Tfoot>
            <Tr>
              <Td
                verticalAlign="top"
                rowSpan={
                  2 + (total === subTotal ? 0 : 1) + (taxAmount === 0 ? 0 : 1)
                }
              >
                <Button size="xs" onClick={props.createStudent}>
                  Add student
                </Button>
              </Td>
              <Td textAlign="right">
                {course.taxType ? "Subtotal" : "Monthly Total"}
              </Td>
              <Td textAlign="right" h={10}>
                {displayCents(subTotal, course.currencyCode)}
              </Td>
            </Tr>
            {taxAmount > 0 && (
              <Tr>
                <Td textAlign="right">Tax</Td>
                <Td textAlign="right" h={10}>
                  {displayCents(course.taxCents!, course.currencyCode)}
                </Td>
              </Tr>
            )}
            <Tr>
              <Td textAlign="right">
                <strong>Monthly Total</strong>
              </Td>
              <Td textAlign="right" h={10}>
                <strong>
                  {displayCents(
                    course.taxCents! + course.amountCents * students.length,
                    course.currencyCode
                  )}
                </strong>
              </Td>
            </Tr>
            <Tr>
              <Td textAlign="right">
                <strong>Due Today</strong>
              </Td>
              <Td textAlign="right" h={10}>
                <strong>{displayCents(0)}</strong>
              </Td>
            </Tr>
            <Tr></Tr>
          </Tfoot>
        </Table>
      </Box>

      <form onSubmit={handleSubmit(onSubmit, onErrors)}>
        <Heading size="md">Parent/guardian details</Heading>
        <Stack mt={2} mb={10}>
          <FlippyStack>
            {requiredField("givenNames", "First name")}
            {requiredField("familyName", "Last name")}
          </FlippyStack>
          <FlippyStack>
            {requiredField("email", "Email address", "email")}
            {requiredField(
              "phone",
              "Phone number",
              "tel",
              30,
              validatePhoneNumber,
              "Must be a valid phone number."
            )}
            {optionalField("company", "Company")}
          </FlippyStack>
        </Stack>

        <Heading size="md">Create a password</Heading>
        <Stack mt={2} mb={10}>
          <FormControl
            id="password"
            isRequired
            isInvalid={!!errors["password"]}
          >
            <FormLabel mt={2} mb={1}>
              Password
            </FormLabel>
            <InputGroup>
              <Input
                pr={16}
                type={showPassword ? "text" : "password"}
                bgColor="white"
                {...register("password", {
                  required: true,
                  minLength: 8,
                  maxLength: 60,
                  validate: validatePassword,
                })}
              />
              <InputRightElement w={16} mr={1}>
                <Button
                  size="xs"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </Button>
              </InputRightElement>
            </InputGroup>

            {/* use role="alert" to announce the error message */}
            {errors.password?.type === "required" && (
              <FormErrorMessage>This is required.</FormErrorMessage>
            )}
            {errors.password?.type === "minLength" && (
              <FormErrorMessage>Minimum 8 characters.</FormErrorMessage>
            )}
            {errors.password?.type === "maxLength" && (
              <FormErrorMessage>Maximum 60 characters.</FormErrorMessage>
            )}
            {errors.password?.type === "validate" && (
              <FormErrorMessage>
                Passwords must contain at least one letter and one number.
              </FormErrorMessage>
            )}
          </FormControl>
        </Stack>

        <Heading size="md">Billing address</Heading>
        <Stack mt={2} mb={10}>
          {requiredField("addressLine1", "Street address")}
          {optionalField("addressLine2", undefined)}
          <FlippyStack>
            {requiredField(
              "addressPostalCode",
              "Postal code",
              "text",
              100,
              validatePostalCode,
              "Must be a valid postal or zip code."
            )}
            {requiredField("addressCity", "City")}
            {requiredField("addressProvince", "Province")}
            {requiredField("addressCountry", "Country")}
          </FlippyStack>
        </Stack>

        <Heading size="md" mt={6} mb={2}>
          Terms and payment
        </Heading>
        <FormControl id="card" isRequired>
          <Flex direction="row" mb={2}>
            <FormLabel mb={0}>Card details</FormLabel>
            <Spacer />
            <HStack spacing={2} fontSize="xl">
              <Icon as={FaCcVisa} />
              <Icon as={FaCcAmex} />
              <Icon as={FaCcMastercard} />
              <Icon as={BiLockAlt} />
            </HStack>
          </Flex>
          <Box
            bgColor="white"
            borderColor="gray.200"
            borderWidth="1px"
            borderStyle="solid"
            px={4}
            py={3}
            borderRadius="md"
          >
            <CardElement
              onChange={onCardChanged}
              options={CARD_ELEMENT_OPTIONS}
            />
          </Box>
        </FormControl>

        <Stack mt={6} mb={2}>
          <Checkbox
            size="lg"
            colorScheme="green"
            alignItems="top"
            isChecked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          >
            <Text fontSize="sm">
              I agree to the{" "}
              <Link
                href="https://makerkids.com/terms-and-conditions/"
                isExternal
                colorScheme="blue"
              >
                terms and conditions
              </Link>
              . I understand that I wil receive 1 class free to see if this
              program is right for my child(ren). If I am not satisfied, I can
              cancel my trial anytime at no cost by contacting MakerKids. Free
              trials are one per child. If I do not cancel my subscription
              within 7 days after the first session, I will be charged monthly
              until I cancel or change my subscription.
            </Text>
          </Checkbox>
        </Stack>

        <Modal
          closeOnOverlayClick={false}
          isOpen={!!checkoutError}
          onClose={clearError}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create your account</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <Text>An error occurred while checking out.</Text>
              <Code>{checkoutError}</Code>
            </ModalBody>

            <ModalFooter>
              <Button onClick={clearError}>Ok</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <SubmitButton
          control={control}
          flavourText={FLAVOUR_TEXT}
          caption="Start your free trial"
          isLoading={isSubmitting}
          isEnabled={acceptedTerms && cardIsValid}
        />
      </form>
    </>
  );
};

const Checkout = (props: PropTypes) => {
  return (
    <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
      <CheckoutForm {...props} />
    </Elements>
  );
};

export default Checkout;
